import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api, endpoints } from '../apiConfig';
import toast from 'react-hot-toast';
import ArtworkCard from '../components/ArtworkCard';
import AIEnhanceField from '../components/AIEnhanceField';
import RichTextEditor from '../components/RichTextEditor';
import RichTextDisplay from '../components/RichTextDisplay';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from './Dashboard.module.css';
import ExhibitionManager from '../components/ExhibitionManager';
import RepresentationManager from '../components/RepresentationManager';
import ResumeDisplay from '../components/ResumeDisplay';
import { useStripeOnboardingStatus } from '../components/PaymentSettings';
import AddressAutocomplete from '../components/AddressAutocomplete';
import Resume from '../components/Resume';
import PublishGalleryButton from '../components/PublishGalleryButton';
import ShippingAddress from '../components/ShippingAddress';
import ArtworkImageGallery from '../components/ArtworkImageGallery';

// Types
const ArtworkStatus = {
  DRAFT: 'draft',
  READY_TO_PUBLISH: 'ready_to_publish',
  PUBLISHED: 'published'
};

const Dashboard = () => {
  // State
  const [user, setUser] = useState(null);
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedArtwork, setSelectedArtwork] = useState(null);

  useEffect(() => {
    if (!selectedArtwork) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [selectedArtwork]);

  const navigate = useNavigate();
  const { hash } = useLocation();
  const { currentUser, loading: authLoading } = useAuth();
  const { status: stripeStatus, loading: stripeLoading, error: stripeError, refetch: refetchStripe } = useStripeOnboardingStatus();
  const [isEditing, setIsEditing] = useState(false);
  const [editedProfile, setEditedProfile] = useState(null);

  // Menu links like /dashboard#exhibitions arrive before the sections render,
  // so the browser's own jump misses them.
  useEffect(() => {
    if (!hash || !user || loading) return undefined;
    const frame = requestAnimationFrame(() => {
      document.getElementById(decodeURIComponent(hash.slice(1)))
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    return () => cancelAnimationFrame(frame);
  }, [hash, user, loading]);

  // Fetch user data and artworks
  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser) {
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // First get the user profile
        const userResponse = await api.get(endpoints.user.profile);
        
        // Try to fetch resume (structured and raw) - make this optional
        let resumeData = { structured_data: null, raw_text: '' };
        try {
          const resumeResponse = await api.get('/resume');
          resumeData = resumeResponse.data;
        } catch (resumeError) {
          console.warn('Resume fetch failed (non-critical):', resumeError);
          // Resume fetch failure is non-critical, continue with empty resume data
        }
        
        setUser({
          ...userResponse.data,
          resume: resumeData.structured_data ? JSON.stringify(resumeData.structured_data) : '',
          resume_text: resumeData.raw_text || ''
        });
        
        // Then fetch artworks
        const artworksResponse = await api.get(endpoints.artwork.list);
        console.log('Fetched artworks:', artworksResponse.data);
        setArtworks(artworksResponse.data || []);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message);
        
        if (err.response?.status === 401) {
          navigate('/login');
        }
      } finally {
        setLoading(false);
      }
    };

    // Only fetch if we have a currentUser and we're not already loading
    if (currentUser && !loading && !authLoading) {
      fetchData();
    }
  }, [currentUser, authLoading, navigate]);



  const startEditing = () => {
    console.log('🔧 Starting edit mode with user data:', user);
    setEditedProfile({
      name: user.name || '',
      bio: user.bio || '',
      resume: user.resume || '', // Re-enabled with proper JSON handling
      resume_text: user.resume_text || '', // Re-enabled with proper JSON handling
      artist_statement: user.artist_statement || '',
      location: user.location || '',
      website: user.website || '',
      instagram: user.social_links?.instagram || '',
      facebook: user.social_links?.facebook || '',
      twitter: user.social_links?.twitter || '',
      tiktok: user.social_links?.tiktok || '',
      email_preferences: user.email_preferences || {},
      // Include all shipping address fields
      shipping_name: user.shipping_name || '',
      shipping_phone: user.shipping_phone || '',
      shipping_address_line1: user.shipping_address_line1 || '',
      shipping_address_line2: user.shipping_address_line2 || '',
      shipping_city: user.shipping_city || '',
      shipping_state: user.shipping_state || '',
      shipping_postal_code: user.shipping_postal_code || '',
      shipping_country: user.shipping_country || 'US',
    });
    console.log('🔧 Edit state initialized with shipping data:', {
      shipping_name: user.shipping_name,
      shipping_address_line1: user.shipping_address_line1,
      shipping_city: user.shipping_city
    });
    console.log('🔧 Resume data initialized for editing:', {
      resume: user.resume ? 'Present' : 'Empty',
      resume_text: user.resume_text ? 'Present' : 'Empty',
      resume_length: user.resume ? user.resume.length : 0,
      resume_text_length: user.resume_text ? user.resume_text.length : 0
    });
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditedProfile(null);
  };

  const handleFieldChange = (field, value) => {
    setEditedProfile(prev => ({ ...prev, [field]: value }));
  };

  const handleShippingAddressChange = (shippingData) => {
    console.log('🚚 Shipping address changed:', shippingData);
    setEditedProfile(prev => { 
      const updated = {
        ...prev,
        ...shippingData
      };
      console.log('🚚 Updated editedProfile:', updated);
      return updated;
    });
  };

  // Separate function to fetch user profile data
  const fetchUserProfile = async () => {
    try {
      console.log('🔄 Fetching updated user profile...');
      const userResponse = await api.get(endpoints.user.profile);
      
      // Try to fetch resume (structured and raw) - make this optional
      let resumeData = { structured_data: null, raw_text: '' };
      try {
        const resumeResponse = await api.get('/resume');
        resumeData = resumeResponse.data;
      } catch (resumeError) {
        console.warn('Resume fetch failed (non-critical):', resumeError);
        // Resume fetch failure is non-critical, continue with empty resume data
      }
      
      const updatedUser = {
        ...userResponse.data,
        resume: resumeData.structured_data ? JSON.stringify(resumeData.structured_data) : '',
        resume_text: resumeData.raw_text || ''
      };
      
      console.log('✅ Updated user profile:', updatedUser);
      setUser(updatedUser);
      return updatedUser;
    } catch (error) {
      console.error('❌ Error fetching user profile:', error);
      throw error;
    }
  };

  const saveProfileChanges = async () => {
    try {
      console.log('🔍 Saving profile changes with data:', editedProfile);

      console.log('🔍 Shipping address fields:', {
        shipping_name: editedProfile.shipping_name,
        shipping_phone: editedProfile.shipping_phone,
        shipping_address_line1: editedProfile.shipping_address_line1,
        shipping_address_line2: editedProfile.shipping_address_line2,
        shipping_city: editedProfile.shipping_city,
        shipping_state: editedProfile.shipping_state,
        shipping_postal_code: editedProfile.shipping_postal_code,
        shipping_country: editedProfile.shipping_country,
      });
      
      const response = await api.put(endpoints.user.profile, editedProfile);
      console.log('✅ Profile save response:', response.data);
      
      // Fetch updated user data from server
      console.log('🔄 Fetching updated user profile...');
      const userResponse = await api.get(endpoints.user.profile);
      
      // Try to fetch resume (structured and raw) - make this optional
      let resumeData = { structured_data: null, raw_text: '' };
      try {
        const resumeResponse = await api.get('/resume');
        resumeData = resumeResponse.data;
      } catch (resumeError) {
        console.warn('Resume fetch failed (non-critical):', resumeError);
        // Resume fetch failure is non-critical, continue with empty resume data
      }
      
      const updatedUser = {
        ...userResponse.data,
        resume: resumeData.structured_data ? JSON.stringify(resumeData.structured_data) : '',
        resume_text: resumeData.raw_text || ''
      };
      
      console.log('✅ Updated user profile loaded:', updatedUser);
      // Update the local user state
      setUser(updatedUser);
      
      // Exit editing mode
      setIsEditing(false);
      
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('❌ Error saving profile:', error);
      toast.error('Failed to save profile changes');
    }
  };

  const handleEdit = (artwork) => {
    navigate(`/artwork/${artwork.id}/edit`);
  };

  const handleView = (artwork) => {
    setSelectedArtwork(artwork);
  };

  const handleClosePreview = () => {
    setSelectedArtwork(null);
  };

  const handleDelete = async (artwork) => {
    try {
      await api.delete(endpoints.artwork.delete(artwork.id));
      setArtworks(prevArtworks => 
        prevArtworks.filter(a => a.id !== artwork.id)
      );
      toast.success('Artwork deleted successfully');
    } catch (error) {
      console.error('Error deleting artwork:', error);
      toast.error(error.response?.data?.message || 'Failed to delete artwork');
      throw error;
    }
  };

  const handlePublish = async (artwork) => {
    // Block publishing if Stripe onboarding is incomplete
    if (!stripeLoading && (!stripeStatus || stripeStatus.status !== 'active')) {
      toast.error(
        <div style={{
          background: 'var(--color-danger-light)',
          color: 'var(--color-danger)',
          border: '1px solid var(--color-danger)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--spacing-md)',
          textAlign: 'center',
          fontWeight: 500
        }}>
          You must complete your Stripe onboarding and verify your identity before you can publish artwork.<br />
          <button
            type="button"
            style={{
              marginTop: 'var(--spacing-sm)',
              background: 'var(--color-primary)',
              color: 'var(--color-white)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--spacing-xs) var(--spacing-lg)',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'var(--transition-base)'
            }}
            onClick={async () => {
              try {
                const response = await api.post('/stripe/connect/account-link');
                window.location.href = response.data.url;
              } catch (err) {
                toast.error('Error launching Stripe onboarding: ' + err.message);
              }
            }}
          >
            Finish Stripe Setup
          </button>
        </div>,
        { duration: 10000 }
      );
      return;
    }
    try {
      const response = await api.post(
        endpoints.artwork.publish(artwork.id),
        { confirmedPrice: artwork.price }
      );

      setArtworks(prevArtworks =>
        prevArtworks.map(a =>
          a.id === artwork.id ? {
            ...a,
            status: 'published',
            shopify_handle: response.data.shopify_handle,
            shopify_variant_id: response.data.shopify_variant_id
          } : a
        )
      );

      toast.success('Artwork published successfully');
    } catch (error) {
      console.error('Error publishing artwork:', error);
      if (error.response?.data?.stripe_status) {
        toast.error(
          <div>
            <p>{error.response.data.error}</p>
            <a href="/payment-settings" style={{ color: '#fff', textDecoration: 'underline' }}>
              Go to Payment Settings
            </a>
          </div>,
          { autoClose: 10000 }
        );
      } else {
        toast.error(error.response?.data?.error || 'Failed to publish artwork');
      }
      throw error;
    }
  };

  const handleUpdateSoldStatus = async (artwork, isSold) => {
    if (!artwork || !artwork.id) {
      toast.error('Artwork is missing or invalid. Please refresh the page or contact support.');
      return;
    }
    try {
      const artworkId = artwork.id;
      await api.patch(
        endpoints.artwork.updateSoldStatus(artworkId),
        { is_sold: isSold }
      );

      setArtworks(prevArtworks => 
        prevArtworks.map(a =>
          a.id === artworkId ? { ...a, is_sold: isSold } : a
        )
      );

      toast.success(`Artwork marked as ${isSold ? 'Sold' : 'Available'}`);
    } catch (error) {
      console.error('Error updating artwork sold status:', error);
      toast.error(error.response?.data?.message || 'Failed to update artwork sold status');
      throw error;
    }
  };

  const handleUpdateStatus = async (artworkId, newStatus) => {
    try {
      await api.patch(
        endpoints.artwork.updateStatus(artworkId),
        { status: newStatus }
      );

      setArtworks(prevArtworks => 
        prevArtworks.map(a =>
          a.id === artworkId ? { ...a, status: newStatus } : a
        )
      );

      toast.success(`Artwork marked as ${newStatus.split('_').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ')}`);
    } catch (error) {
      console.error('Error updating artwork status:', error);
      toast.error(error.response?.data?.message || 'Failed to update artwork status');
      throw error;
    }
  };

  // Show loading state while auth is initializing
  if (authLoading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner}></div>
        <p>Loading your dashboard...</p>
      </div>
    );
  }

  // Show error state if there's an error
  if (error) {
    return (
      <div className={styles.error}>
        <h2>Something went wrong</h2>
        <p>{error}</p>
        <button onClick={() => window.location.reload()} className="btn-primary">
          Try Again
        </button>
      </div>
    );
  }

  // Show loading state while fetching data
  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner}></div>
        <p>Loading your dashboard...</p>
      </div>
    );
  }

  // Profile data is still loading. A signed-in gallery can land here with an
  // empty profile for a moment; that is not a logged-out session.
  if (!user) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner}></div>
        <p>Loading your dashboard...</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.profileHeader}>
            <div>
              <h1>Welcome, {user.name}</h1>
              <p className={styles.userRole}>{user.role.charAt(0).toUpperCase() + user.role.slice(1)}</p>
            </div>
            <div className={styles.profileActions}>
              {isEditing ? (
                <>
                  <button onClick={cancelEditing} className={styles.secondaryButton}>Cancel</button>
                  <button onClick={saveProfileChanges} className={styles.saveButton}>Save Changes</button>
                </>
              ) : (
                <button onClick={startEditing} className={styles.secondaryButton}>Edit Profile</button>
              )}
            </div>
          </div>
          {/* Profile Section */}
          <div id="profile" className={styles.profileSection}>
            <div className={styles.profileField}>
              <label>Bio</label>
              {isEditing ? (
                <RichTextEditor
                  value={editedProfile.bio || ''}
                  onChange={value => handleFieldChange('bio', value)}
                  section="bio"
                  placeholder="Tell collectors about yourself, your background, and your artistic journey..."
                />
              ) : (
                <RichTextDisplay 
                  content={user.bio}
                  placeholder="Tell collectors about yourself, your background, and your artistic journey..."
                />
              )}
            </div>

            {user.role === 'artist' && (
              <>
                <div className={styles.profileField}>
                  <label>Resume</label>
                  {isEditing ? (
                    <Resume
                      value={editedProfile.resume || ''}
                      rawText={editedProfile.resume_text || ''}
                      onChange={resume => handleFieldChange('resume', resume)}
                      onRawTextChange={raw => handleFieldChange('resume_text', raw)}
                    />
                  ) : (
                    <ResumeDisplay data={user.resume ? JSON.parse(user.resume) : null} rawText={user.resume_text || ''} />
                  )}
                </div>

                <div className={styles.profileField}>
                  <label>Artist Statement</label>
                  {isEditing ? (
                    <RichTextEditor
                      value={editedProfile.artist_statement || ''}
                      onChange={value => handleFieldChange('artist_statement', value)}
                      section="artist_statement"
                      placeholder="Share your artistic vision, inspiration, and creative process..."
                    />
                  ) : (
                    <RichTextDisplay 
                      content={user.artist_statement}
                      placeholder="Share your artistic vision, inspiration, and creative process..."
                    />
                  )}
                </div>
              </>
            )}

            {user.role === 'curator' && (
              <>
                <div className={styles.profileField}>
                  <label>Curator Statement</label>
                  {isEditing ? (
                    <textarea
                      value={editedProfile.curator_statement || ''}
                      onChange={e => handleFieldChange('curator_statement', e.target.value)}
                      placeholder="Share your curatorial vision and approach..."
                      className={styles.editTextarea}
                    />
                  ) : (
                    <p>{user.curator_statement || 'Share your curatorial vision and approach...'}</p>
                  )}
                </div>
                <div className={styles.profileField}>
                  <label>Commission Rate</label>
                  {isEditing ? (
                    <input
                      type="number"
                      value={editedProfile.commission_rate || ''}
                      onChange={e => handleFieldChange('commission_rate', e.target.value)}
                      placeholder="Set your commission rate for sales"
                      className={styles.editInput}
                    />
                  ) : (
                    <p>{user.commission_rate ? `${user.commission_rate}%` : 'Set your commission rate for sales.'}</p>
                  )}
                </div>
              </>
            )}

            {user.role === 'gallery' && (
              <>
                <div className={styles.profileField}>
                  <label>Gallery Statement</label>
                  <p>{user.gallery_statement || 'Share your gallery\'s mission and focus...'}</p>
                </div>
                <div className={styles.profileField}>
                  <label>Gallery Hours</label>
                  <p>{user.gallery_hours || 'Add your gallery\'s operating hours.'}</p>
                </div>
              </>
            )}

            <div className={styles.profileField}>
              <label>Location</label>
              {isEditing ? (
                <AddressAutocomplete
                  value={editedProfile.location || ''}
                  onChange={location => handleFieldChange('location', location)}
                  placeholder="Enter your location"
                />
              ) : (
                <p>{user.location || 'Add your location to help collectors find you.'}</p>
              )}
            </div>

            {/* Shipping Address - Only for users who ship (artists, galleries, dealers) */}
            {['artist', 'gallery', 'dealer'].includes(user.role) && (
              <div className={styles.profileField}>
                <ShippingAddress
                  shippingData={{
                    shipping_name: isEditing ? editedProfile.shipping_name : user.shipping_name,
                    shipping_phone: isEditing ? editedProfile.shipping_phone : user.shipping_phone,
                    shipping_address_line1: isEditing ? editedProfile.shipping_address_line1 : user.shipping_address_line1,
                    shipping_address_line2: isEditing ? editedProfile.shipping_address_line2 : user.shipping_address_line2,
                    shipping_city: isEditing ? editedProfile.shipping_city : user.shipping_city,
                    shipping_state: isEditing ? editedProfile.shipping_state : user.shipping_state,
                    shipping_postal_code: isEditing ? editedProfile.shipping_postal_code : user.shipping_postal_code,
                    shipping_country: isEditing ? editedProfile.shipping_country : user.shipping_country,
                  }}
                  onChange={handleShippingAddressChange}
                  isEditing={isEditing}
                  userRole={user.role}
                />
              </div>
            )}

            <div className={styles.profileField}>
              <label>Social Media</label>
              {isEditing ? (
                <div className={styles.socialLinksEdit}>
                  <input
                    type="url"
                    value={editedProfile.website || ''}
                    onChange={e => handleFieldChange('website', e.target.value)}
                    placeholder="https://your-website.com"
                    className={styles.editInput}
                  />
                  <input
                    type="url"
                    value={editedProfile.instagram || ''}
                    onChange={e => handleFieldChange('instagram', e.target.value)}
                    placeholder="https://instagram.com/your-handle"
                    className={styles.editInput}
                  />
                  <input
                    type="url"
                    value={editedProfile.facebook || ''}
                    onChange={e => handleFieldChange('facebook', e.target.value)}
                    placeholder="https://facebook.com/your-page"
                    className={styles.editInput}
                  />
                  <input
                    type="url"
                    value={editedProfile.twitter || ''}
                    onChange={e => handleFieldChange('twitter', e.target.value)}
                    placeholder="https://twitter.com/your-handle"
                    className={styles.editInput}
                  />
                  <input
                    type="url"
                    value={editedProfile.tiktok || ''}
                    onChange={e => handleFieldChange('tiktok', e.target.value)}
                    placeholder="https://tiktok.com/@your-handle"
                    className={styles.editInput}
                  />
                </div>
              ) : (
                <div className={styles.socialLinks}>
                  {user.website && (
                    <a 
                      href={user.website.startsWith('http') ? user.website : `https://${user.website}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className={styles.socialLink}
                    >
                      Website
                    </a>
                  )}
                  {user.instagram && (
                    <a 
                      href={user.instagram.startsWith('http') ? user.instagram : `https://${user.instagram}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className={styles.socialLink}
                    >
                      Instagram
                    </a>
                  )}
                  {user.facebook && (
                    <a 
                      href={user.facebook.startsWith('http') ? user.facebook : `https://${user.facebook}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className={styles.socialLink}
                    >
                      Facebook
                    </a>
                  )}
                  {user.twitter && (
                    <a 
                      href={user.twitter.startsWith('http') ? user.twitter : `https://${user.twitter}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className={styles.socialLink}
                    >
                      Twitter
                    </a>
                  )}
                  {user.tiktok && (
                    <a 
                      href={user.tiktok.startsWith('http') ? user.tiktok : `https://${user.tiktok}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className={styles.socialLink}
                    >
                      TikTok
                    </a>
                  )}
                  {!user.website && !user.instagram && !user.facebook && !user.twitter && !user.tiktok && (
                    <p className={styles.textMuted}>Add your social media links to connect with collectors.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Online Gallery Section for Artists, Galleries, and Admins */}
      {(user.role === 'artist' || user.role === 'gallery' || user.role === 'admin') && (
        <div id="gallery" className={styles.cardSection}>
          <h2 className={styles.sectionTitle}>
            Your Online Gallery
          </h2>
          <p className={styles.sectionDescription}>
            {user.role === 'gallery' 
              ? 'Create a beautiful, shareable web page featuring all your gallery\'s artworks and artists on Art Emerge. Once published, you\'ll get a unique link you can share with collectors, visitors, or on social media.'
              : user.role === 'admin'
              ? 'Create a beautiful, shareable web page featuring artworks on Art Emerge. As an admin, you can manage artworks and galleries for testing and administration purposes.'
              : 'Create a beautiful, shareable web page featuring all your artworks on Art Emerge. Once published, you\'ll get a unique link you can share with collectors, fans, or on social media.'
            }
          </p>
          <PublishGalleryButton />
        </div>
      )}

      <main className={styles.content}>
        {(user.role === 'artist' || user.role === 'gallery' || user.role === 'admin') ? (
          <>
            {/* Shipping & Labels Section */}
            <section id="shipping" className={styles.cardSection}>
              <div className={styles.sectionHeader}>
                <h2>Shipping & Labels</h2>
                <div className={styles.sectionActions}>
                  <button className={styles.secondaryButton} onClick={() => navigate('/shipping')}>
                    Ship Artwork
                  </button>
                </div>
              </div>
              <p className={styles.sectionDescription}>
                {user.role === 'gallery' 
                  ? 'Print discounted shipping labels, manage packaging, and streamline fulfillment for your gallery\'s artworks.'
                  : user.role === 'admin'
                  ? 'Print discounted shipping labels, manage packaging, and streamline fulfillment for testing and administrative purposes.'
                  : 'Print discounted shipping labels, manage packaging, and streamline fulfillment for your artworks.'
                }
              </p>
            </section>

            {/* Artworks Section */}
            <section id="artworks" className={styles.cardSection}>
              <div className={styles.sectionHeader}>
                <h2>{user.role === 'gallery' ? 'Gallery Artworks' : user.role === 'admin' ? 'Manage Artworks' : 'Your Artworks'}</h2>
                <div className={styles.sectionActions}>
                  <button className={styles.secondaryButton} onClick={() => navigate('/upload')}>
                    {user.role === 'gallery' ? 'Add New Artwork' : user.role === 'admin' ? 'Upload/Manage Artwork' : 'Upload New Artwork'}
                  </button>
                </div>
              </div>

              {artworks.length === 0 ? (
                <div className={styles.emptyState}>
                  <h3>{user.role === 'gallery' ? 'No artworks in gallery yet' : user.role === 'admin' ? 'No artworks to manage yet' : 'No artworks yet'}</h3>
                  <p>{user.role === 'gallery' ? 'Start by adding your first artwork to the gallery' : user.role === 'admin' ? 'Start by uploading an artwork for testing or management' : 'Start by uploading your first artwork'}</p>
                  <button className="btn-primary" onClick={() => navigate('/upload')}>
                    {user.role === 'gallery' ? 'Add Artwork' : user.role === 'admin' ? 'Upload Artwork' : 'Upload Artwork'}
                  </button>
                </div>
              ) : (
                <div className={styles.artworkGrid}>
                  {artworks.map(artwork => (
                    <ArtworkCard
                      key={artwork.id}
                      artwork={{
                        ...artwork,
                        type: 'artwork'
                      }}
                      onEdit={() => handleEdit(artwork)}
                      onView={() => handleView(artwork)}
                      onDelete={() => handleDelete(artwork)}
                      onPublish={() => handlePublish(artwork)}
                      onUpdateStatus={(newStatus) => handleUpdateStatus(artwork.id, newStatus)}
                      onUpdateSoldStatus={(isSold) => handleUpdateSoldStatus(artwork, isSold)}
                    />
                  ))}
                </div>
              )}
            </section>

            {(user.role === 'gallery' || user.role === 'artist') && (
              <RepresentationManager role={user.role} />
            )}

            <div id="exhibitions">
              <ExhibitionManager isHouseGallery={Boolean(user.is_house_gallery)} />
            </div>
          </>
        ) : user.role === 'curator' ? (
          <div id="exhibitions">
            <ExhibitionManager />
          </div>
        ) : (
          <>
            <section id="collection" className={styles.cardSection}>
              <div className={styles.sectionHeader}>
                <h2>Your Collection</h2>
                <div className={styles.sectionActions}>
                  <button className={styles.secondaryButton} onClick={() => navigate('/browse')}>
                    Browse Artworks
                  </button>
                </div>
              </div>

              {artworks.length === 0 ? (
                <div className={styles.emptyState}>
                  <h3>Your collection is empty</h3>
                  <p>Start building your art collection by browsing available artworks</p>
                  <button className="btn-primary" onClick={() => navigate('/browse')}>
                    Browse Artworks
                  </button>
                </div>
              ) : (
                <div className={styles.artworkGrid}>
                  {artworks.map(artwork => (
                    <ArtworkCard
                      key={artwork.id}
                      artwork={{
                        ...artwork,
                        type: 'collection'
                      }}
                      onView={() => handleView(artwork)}
                    />
                  ))}
                </div>
              )}
            </section>

            <section className={styles.cardSection}>
              <div className={styles.sectionHeader}>
                <h2>Recommended Artists</h2>
              </div>
              <div className={styles.comingSoon}>
                <h3>Coming Soon</h3>
                <p>We're working on personalized artist recommendations based on your interests and collection.</p>
              </div>
            </section>
          </>
        )}
      </main>

      {selectedArtwork && (
        <div
          className={`${styles.modal} ae-dialog-scroll`}
          onClick={(e) => {
            if (e.target !== e.currentTarget) return;
            if (e.offsetX > e.currentTarget.clientWidth || e.offsetY > e.currentTarget.clientHeight) return;
            handleClosePreview();
          }}
        >
          <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
            <button className={styles.closeButton} onClick={handleClosePreview}>×</button>
            <div className={styles.previewImageContainer}>
              <ArtworkImageGallery
                images={selectedArtwork.images || []}
                title={selectedArtwork.title}
                showThumbnails={true}
                className={styles.previewGallery}
              />
            </div>
            <div className={styles.artworkDetails}>
              <div className={styles.titleSection}>
                <h2>{selectedArtwork.title}</h2>
                <span className={styles.status}>{selectedArtwork.status}</span>
              </div>
              
              {/* Basic Details Section */}
              <div className={styles.section}>
                <h3>Artwork Details</h3>
                {selectedArtwork.description && (
                  <p className={styles.description}>{selectedArtwork.description}</p>
                )}
                <div className={styles.detailsGrid}>
                  {selectedArtwork.medium && (
                    <div className={styles.detailItem}>
                      <span className={styles.label}>Medium</span>
                      <span>{selectedArtwork.medium}</span>
                    </div>
                  )}
                  {selectedArtwork.dimensions && (
                    <div className={styles.detailItem}>
                      <span className={styles.label}>Dimensions</span>
                      <span>
                        {selectedArtwork.dimensions.width}" × {selectedArtwork.dimensions.height}"
                        {selectedArtwork.dimensions.depth && ` × ${selectedArtwork.dimensions.depth}"`}
                      </span>
                    </div>
                  )}
                  {selectedArtwork.year_created && (
                    <div className={styles.detailItem}>
                      <span className={styles.label}>Year</span>
                      <span>{selectedArtwork.year_created}</span>
                    </div>
                  )}
                  {selectedArtwork.edition_type && (
                    <div className={styles.detailItem}>
                      <span className={styles.label}>Edition</span>
                      <span>
                        {selectedArtwork.edition_type}
                        {selectedArtwork.edition_number && selectedArtwork.edition_size && 
                          ` (${selectedArtwork.edition_number}/${selectedArtwork.edition_size})`
                        }
                      </span>
                    </div>
                  )}
                  {selectedArtwork.product_type && (
                    <div className={styles.detailItem}>
                      <span className={styles.label}>Product Type</span>
                      <span>{selectedArtwork.product_type}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Price Section */}
              {selectedArtwork.price && (
                <div className={styles.section}>
                  <div className={styles.priceSection}>
                    <h3>Price</h3>
                    <p className={styles.price}>${Math.round(parseFloat(selectedArtwork.price))}</p>
                    {selectedArtwork.ai_analysis?.price_range && (
                      <p className={styles.priceRange}>
                        Estimated Value Range: ${selectedArtwork.ai_analysis.price_range.min} - ${selectedArtwork.ai_analysis.price_range.max}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* AI Analysis Section */}
              {selectedArtwork.ai_analysis && (
                <div className={styles.section}>
                  <h3>AI Analysis</h3>
                  {selectedArtwork.ai_analysis.style && (
                    <div className={styles.aiSection}>
                      <h4>Style Analysis</h4>
                      <p>{selectedArtwork.ai_analysis.style}</p>
                    </div>
                  )}
                  {selectedArtwork.ai_analysis.description && (
                    <div className={styles.aiSection}>
                      <h4>Market Analysis</h4>
                      <p>{selectedArtwork.ai_analysis.description}</p>
                    </div>
                  )}
                  {selectedArtwork.ai_analysis.tags && selectedArtwork.ai_analysis.tags.length > 0 && (
                    <div className={styles.aiSection}>
                      <h4>Tags</h4>
                      <div className={styles.tags}>
                        {selectedArtwork.ai_analysis.tags.map((tag, index) => (
                          <span key={index} className={styles.tag}>{tag}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Shopify Details */}
              {selectedArtwork.shopify_handle && (
                <div className={styles.section}>
                  <h3>Store Details</h3>
                  <div className={styles.detailsGrid}>
                    <div className={styles.detailItem}>
                      <span className={styles.label}>Product Handle</span>
                      <span>{selectedArtwork.shopify_handle}</span>
                    </div>
                    {selectedArtwork.shopify_variant_id && (
                      <div className={styles.detailItem}>
                        <span className={styles.label}>Variant ID</span>
                        <span>{selectedArtwork.shopify_variant_id}</span>
                      </div>
                    )}
                  </div>
                  <a 
                    href={`https://${process.env.REACT_APP_SHOPIFY_STORE_URL}/products/${selectedArtwork.shopify_handle}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.shopifyLink}
                  >
                    View in Store
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard; 